import { Checkbox, Popover, RadioGroup } from '@systemeio/ui'
import { StatisticsFilterInterface } from 'modules/mailing/email-statistics/types/email-statistics-filter-interface'
import { BooleanStrEnum } from 'shared/enums/boolean-str-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface EmailStatisticsStatusFilterProps {
  filter: StatisticsFilterInterface
  setFilter: React.Dispatch<React.SetStateAction<StatisticsFilterInterface>>
}

export const EmailStatisticsStatusFilter = ({
  filter,
  setFilter,
}: EmailStatisticsStatusFilterProps) => {
  const { t } = useLocoTranslation()
  const statusFilterCount = Object.values(filter).filter(value =>
    Object.values(BooleanStrEnum).includes(value),
  ).length
  return (
    <Popover
      className={
        'ring-brand-primary main-transition-colors bg-brand-primary text-background-secondary font-inter  hover:bg-brand-primary-hover h-[42px] !overflow-visible rounded-lg px-4 py-2.5 text-sm font-semibold ring-offset-1 focus-visible:ring-2'
      }
      position={'bottom'}
      label={
        <div className={'flex flex-col gap-3'}>
          <StatusOption filter={filter} setFilter={setFilter} slug={'opened'} />
          <StatusOption filter={filter} setFilter={setFilter} slug={'clicked'} />
          <StatusOption filter={filter} setFilter={setFilter} slug={'spam'} />
          <StatusOption filter={filter} setFilter={setFilter} slug={'bounced'} />
        </div>
      }
    >
      {() => (
        <>
          {t('global.filter_by')}
          {statusFilterCount > 0 && (
            <span
              className={
                'bg-text-primary absolute right-0 top-0 flex h-[20px] w-[20px] -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full text-xs'
              }
            >
              {statusFilterCount}
            </span>
          )}
        </>
      )}
    </Popover>
  )
}

const StatusOption = ({
  filter,
  setFilter,
  slug,
}: EmailStatisticsStatusFilterProps & {
  slug: keyof Omit<StatisticsFilterInterface, 'contacts'>
}) => {
  const { t } = useLocoTranslation()
  return (
    <>
      <Checkbox
        value={!!filter[slug]}
        onChange={value =>
          setFilter(prev => ({ ...prev, [slug]: value ? BooleanStrEnum.true : undefined }))
        }
        label={t(statusTitles[slug][BooleanStrEnum.true])}
      />
      {!!filter[slug] && (
        <RadioGroup
          optionsWrapperClassName={'pl-5 flex-col !gap-3 !items-start'}
          options={[
            {
              id: BooleanStrEnum.true,
              caption: t(statusTitles[slug][BooleanStrEnum.true]),
            },
            { id: BooleanStrEnum.false, caption: t(statusTitles[slug][BooleanStrEnum.false]) },
          ]}
          selected={filter[slug]}
          onChange={value => {
            setFilter(prev => ({ ...prev, [slug]: value }))
          }}
        />
      )}
    </>
  )
}

const statusTitles: Record<
  keyof Omit<StatisticsFilterInterface, 'contacts'>,
  Record<BooleanStrEnum, string>
> = {
  opened: {
    [BooleanStrEnum.true]: 'dashboard.email_statistics.table.headers.opened',
    [BooleanStrEnum.false]: 'dashboard.email_statistics.table.headers.not_opened',
  },
  spam: {
    [BooleanStrEnum.true]: 'dashboard.email_statistics.table.headers.reported_as_spam',
    [BooleanStrEnum.false]: 'dashboard.email_statistics.table.headers.not_reported_as_spam',
  },
  bounced: {
    [BooleanStrEnum.true]: 'dashboard.email_statistics.table.headers.bounced',
    [BooleanStrEnum.false]: 'dashboard.email_statistics.table.headers.not_bounced',
  },
  clicked: {
    [BooleanStrEnum.true]: 'dashboard.email_statistics.table.headers.clicked',
    [BooleanStrEnum.false]: 'dashboard.email_statistics.table.headers.not_clicked',
  },
}
